import type { ColDef, RowClickedEvent, ValueFormatterParams } from 'ag-grid-enterprise';
import { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { SurplusItemSettingsDto, SurplusType } from '../../../api/generated/client';
import type { SurplusTableData } from '../../../interfaces/simulation-settings';
import { getDateString } from '../../../utils/date-utils';
import { formatAmount } from '../../../utils/renderUtils';
import { AgGridTable } from '../../atoms';
import { SurplusTableDeleteAction } from './SurplusTableDeleteAction';

interface IProps {
    surplusItems: SurplusItemSettingsDto[];
    editItem: (item: SurplusItemSettingsDto) => void;
    deleteItem: (surplusItemId: number) => void;
}

export const SurplusTable = memo<IProps>(({ surplusItems, editItem, deleteItem }) => {
    const { t } = useTranslation();

    const columnDefs: ColDef<SurplusTableData>[] = [
        {
            field: 'surplusType',
            headerName: t('wealthItemType'),
            width: 85,
            valueFormatter: ({ value }: ValueFormatterParams<SurplusTableData, SurplusType>) => (value ? t(value.toLowerCase()) : ''),
        },
        {
            field: 'fromDate',
            headerName: t('fromDate'),
            width: 110,
            headerClass: 'ag-right-aligned-header',
            cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'end', textAlign: 'right' },
            valueFormatter: ({ value }: ValueFormatterParams<SurplusTableData, string>) => getDateString(value),
        },
        {
            field: 'toDate',
            headerName: t('wealthItemToDate'),
            width: 105,
            headerClass: 'ag-right-aligned-header',
            cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'end', textAlign: 'right' },
            valueFormatter: ({ value }: ValueFormatterParams<SurplusTableData, string>) => getDateString(value),
        },
        {
            field: 'amount',
            headerName: t('amount'),
            minWidth: 80,
            flex: 1,
            headerClass: 'ag-right-aligned-header',
            cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'end', textAlign: 'right' },
            valueFormatter: ({ value, data }: ValueFormatterParams<SurplusTableData, number>) =>
                value && data ? formatAmount(value, false, data.currency) : '',
        },
        {
            headerName: t('wealthItemActions'),
            width: 80,
            headerClass: 'ag-right-aligned-header',
            cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'end', textAlign: 'right' },
            cellRenderer: SurplusTableDeleteAction,
        },
    ];

    const data = useMemo<SurplusTableData[]>(
        () =>
            surplusItems.map((item) => ({
                ...item,
                onDelete: () => deleteItem(item.id!),
            })),
        [surplusItems, deleteItem],
    );

    const onRowClick = ({ data, event }: RowClickedEvent<SurplusTableData>) => {
        const target = event?.target as HTMLElement | null;

        if (data && !target?.closest('zf-icon-button')) editItem(data);
    };

    return (
        <AgGridTable
            rowData={data}
            columnDefs={columnDefs}
            defaultColDef={{
                resizable: false,
                sortable: false,
                suppressHeaderMenuButton: true,
                cellStyle: { display: 'flex', alignItems: 'center' },
            }}
            onRowClicked={onRowClick}
        />
    );
});
